import { EllipsisMenuItemStatus } from '@platform/ui-library';
import { FC, useState } from 'react';
import { ToolbarDropdown } from '@app/form_components';
import { useDesignRoles } from '@app/hooks';
import { EDITOR_ROLES, UserCoreRole } from '@app/types';
import UserTypeStorage from '@app/utilities/UserTypeStorage';

export enum UserType {
  designer = 'designer',
  developer = 'developer',
  quality = 'quality',
  client = 'client'
};

const defaultUserType: UserType = UserType.client;

const userTypes = {
  designer: {
    label: 'Designer',
    mapToRole: UserCoreRole.designerDesigner,
    requiredRoles: EDITOR_ROLES
  },
  developer: {
    label: 'Developer',
    mapToRole: UserCoreRole.designerDeveloper,
    requiredRoles: EDITOR_ROLES
  },
  quality: {
    label: 'Quality',
    mapToRole: UserCoreRole.designerQa,
    requiredRoles: EDITOR_ROLES
  },
  client: {
    label: 'Client',
    mapToRole: null,
    requiredRoles: undefined
  }
} satisfies Record<
  UserType,
  {
    label: string;
    mapToRole: UserCoreRole | null;
    requiredRoles?: UserCoreRole[];
  }
>;

export const UserTypeSelector: FC = () => {
  const [storedUserType, setStoredUserType] = useState<UserType>(UserTypeStorage.getUserType());
  const { hasDesignRole } = useDesignRoles();
  const setUserType = (userTypeKey: UserType): void => {
    setStoredUserType(userTypeKey);
    UserTypeStorage.setUserType(userTypeKey);
    document.dispatchEvent(new CustomEvent('ev-update-userType'));
  };

  let initialUserType: UserType | null = null;
  const availableTypes = (
    Object.entries(userTypes).filter(([role, { mapToRole, requiredRoles }]) => {
      const isValid = !requiredRoles || hasDesignRole(requiredRoles);
      if (isValid && mapToRole && hasDesignRole(mapToRole)) {
        initialUserType = role as UserType;
      }
      return isValid;
    }) as [UserType, (typeof userTypes)[UserType]][]
  );

  const currentUserType = storedUserType ?? initialUserType ?? defaultUserType;

  if (availableTypes.length <= 1) {
    return null;
  }

  const items = availableTypes.map(([role, { label }]) => ({
    action: () => setUserType(role),
    label,
    status: (role === currentUserType) ? EllipsisMenuItemStatus.Selected : undefined
  }));

  const triggerLabel = (
    availableTypes.find(([role]) => role === currentUserType)?.[1].label
    ?? userTypes[currentUserType].label
  );

  return (
    <ToolbarDropdown className="selector" items={items}>
      {triggerLabel}
    </ToolbarDropdown>
  );
};
